import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactFlow, { Background, Controls, Edge, getOutgoers, MiniMap, Node, NodeChange, ProOptions, ReactFlowProvider, useReactFlow, useStoreApi } from "reactflow";
import "reactflow/dist/style.css";

import { CreateNewFolder, DeleteForever, DownloadOutlined, Edit, EditOutlined, NoteAdd, PublishOutlined, Report, SaveOutlined, UploadOutlined } from "@mui/icons-material";
import { Box, Button, Container, Divider, IconButton, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { RichTreeView, useTreeViewApiRef } from "@mui/x-tree-view";
import { UseTreeViewItemsPublicAPI } from "@mui/x-tree-view/internals/plugins/useTreeViewItems/useTreeViewItems.types";

import MDEditor from "@uiw/react-md-editor";

import { JSONSchema7 } from "json-schema";
import CustomTooltip from "../../components/common/CustomTooltip";
import { AddDialog, CustomTreeItem, FileType } from "../../components/file-explorer";
import { deleteFileStatesKeys, handleAddNewFile, handleCreateNewFolder, handleDeleteTreeItem, handleEditTreeItemCallBack } from "../../components/file-explorer/utils";
import { edgeTypes } from "../../components/graph/edges";
import useLayout from "../../components/graph/hooks/useLayout";
import useReactFlowHelpers from "../../components/graph/hooks/useReactFlowHelpers";
import { nodeTypes } from "../../components/graph/nodes";
import { uuid } from "../../components/graph/utils";
import { JSONEditor } from "../../components/json-editor";
import { updateSourceDataObject } from "../../components/json-schema-viewer/components";
import { resetSourceDataObject, sourceDataObject } from "../../components/json-schema-viewer/components/SchemaRow/state";
import PrimaryLayout from "../../components/layout/PrimaryLayout";
import BasicInfoModal from "../../components/modals/BasicInfoModal";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { axiosRepoIntance } from "../../services/api-client";
import { ChangeFileOperation, ChangeFilesOptions, GitEntry, GitTreeResponse, RepoCommit } from "../../services/repo-service";
import { Flow_Default_Files, FLOW_FILE_PATH } from "../../utils/constants/flow";
import { base64ToUtf8, convertGitTreeToTreeView, getFilePathsAndContents, OWNER, Repo_Type, utf8ToBase64 } from "../../utils/constants/gitea";
import { getFormattedDate } from "../../utils/constants/text-field";
import { BasicInfoForm } from "../../utils/models/basic-info";
import { CustomTreeViewBaseItem, FilePathContent, FileState } from "../../utils/models/common-models";


const supportedFileType: { [key: string]: FileType } = {
    "json": "json",
    "md": "md",
    "txt": "txt",
    "folder": "folder",
    "file": "file"
}

const fitViewOptions = {
    padding: 0.95,
};

const proOptions: ProOptions = { account: "paid-pro", hideAttribution: true };

interface ReactFlowProProps {
    initNodes: Node[];
    initEdges: Edge[];
    nodeChange: (change: NodeChange[]) => void;
    onNodeClick: (e: any, node: Node) => void;
    // edgeChange: (change: EdgeChange[]) => void;
}

const ReactFlowPro: React.FC<ReactFlowProProps> = ({ initNodes, initEdges, nodeChange, onNodeClick }) => {
    useLayout();

    return (
        <ReactFlow
            minZoom={0.2}
            defaultNodes={initNodes}
            defaultEdges={initEdges}
            fitViewOptions={fitViewOptions}
            proOptions={proOptions}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={nodeChange}
            onNodeClick={onNodeClick}
            fitView
        >
            <MiniMap />
            <Controls />
            <Background />
        </ReactFlow>
    );
};


const AddFlowWrapper: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const apiTreeRef = useTreeViewApiRef();
    const { id: updateFlowId, name: updateFlowName } = useParams();
    const { getNodes, getEdges, setNodes, setEdges } = useReactFlow();
    const { updateRequestNodeData, getRequestNodeSchema } = useReactFlowHelpers();
    const storeApi = useStoreApi();

    const [fileStructure, setFileStructure] = useState<CustomTreeViewBaseItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [basicInfo, setBasicInfo] = useState<BasicInfoForm>({ name: "", description: "", tags: [], });
    const [openBasicInfoModal, setOpenBasicInfoModal] = useState(false);
    const [flowVersion, setFlowVersion] = useState<string>("");
    const [commitsList, setCommitsList] = useState<{ commitId: string; created: string }[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [newFileName, setNewFileName] = useState<string>('');
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [isAddingFolder, setIsAddingFolder] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<string>('');
    const [fileStates, setFileStates] = useState<FileState>({});
    const [gitTreeContent, setGitTreeContent] = useState<GitEntry[]>([]);
    const [isEditModeTreeItem, setIsEditModeTreeItem] = useState<boolean>(false)    // Edit mode for file-explorer
    const [itemsToDeletePath, setItemsToDeletePath] = useState<string[]>([]);       // paths of items to delete file-stucture
    const [openDeleteModalFileStucture, setOpenDeleteModalFileStucture] = useState<boolean>(false);


    const updateBasicInfo = (data: BasicInfoForm) => {
        console.log("basic info: ", data);
        setBasicInfo(data);
    };

    const handleSaveFlow = () => {
        if (!basicInfo.name) {
            alert("Please provide the name.")
            setOpenBasicInfoModal(true)
            return;
        }

        const updatedFileStates = getRequestSchemaAndUpdateFileStates();
        console.log("updated file states===>> ", updatedFileStates);

        const filesData: FilePathContent[] = getFilePathsAndContents(fileStructure, updatedFileStates);
        console.log("paths------", filesData);

        const gitTreePaths = new Set(gitTreeContent.map(item => item.path));

        const files: ChangeFileOperation[] = filesData.map(data => {
            const jsonStringfied = data.path === FLOW_FILE_PATH.flow ? JSON.stringify(data.content, null, 2) : data.content;

            return {
                operation: gitTreePaths.has(data.path) ? 'update' : 'create',
                path: data.path,
                content: utf8ToBase64(jsonStringfied),
            }
        });

        itemsToDeletePath.forEach(path => {
            if (gitTreePaths.has(path)) {
                files.push({ operation: 'delete', path })
            }
        });

        console.log("creating/updating files: ", files);

        creatFlowWithFiles(basicInfo.name, basicInfo.description, files);
    }

    const handleAddFileClick = () => {
        setIsEditModeTreeItem(false);
        setIsAddingFolder(false);
        setIsDialogOpen(true);
    };

    const handleAddFolderClick = () => {
        setIsEditModeTreeItem(false);
        setIsAddingFolder(true);
        setIsDialogOpen(true);
    };

    const handleCancel = () => {
        setIsDialogOpen(false);
        setNewFileName('');
        setNewFolderName("");
    };

    const handleEditTreeItem = () => {
        if (!selectedFile) return alert("Please select an item first.");

        if (Flow_Default_Files.includes(selectedFile)) {
            return alert("Default file names cannot be edited.");
        }

        setIsEditModeTreeItem(true);
        const selectedTreeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(selectedFile);

        if (selectedTreeItem.type === 'folder') {
            setIsAddingFolder(true);
            setIsDialogOpen(true);
            setNewFolderName(selectedTreeItem.label);
        }
        else {
            setIsAddingFolder(false);
            setIsDialogOpen(true);
            setNewFileName(selectedTreeItem.label);
        }
    };

    const getRequestSchemaAndUpdateFileStates = (): FileState => {
        const request_node_data = getRequestNodeSchema();
        console.log('updating requst schema file===>: ', request_node_data);

        if (request_node_data && Object.keys(request_node_data).length > 0) {
            const updatedState = {
                ...fileStates,
                [FLOW_FILE_PATH.request_schema]: JSON.stringify(request_node_data, null, 2)
            }

            setFileStates(updatedState);
            return updatedState;
        }

        return fileStates;
    }

    const updateFlowStateFile = (prevNodes: Node[], value: any) => {
        console.log('previous nodes ======>: ', prevNodes, value);

        return prevNodes.map((node) => {
            let updatedNode = node;
            if (node.type === 'requestNode') {
                updatedNode = { ...node, data: { ...node.data, request_schema: value } }
                console.log('updating request node in updateFlowStateFile ---: ', updatedNode)
            }

            return updatedNode;
        })
    }

    const createPlaceholderNodes = useCallback((nodesToWork: Node[]) => {
        const placeholderNodes: Node[] = [];
        const placeholderEdges: Edge[] = [];

        nodesToWork.forEach(node => {
            const childPlaceholderId = uuid();

            const childPlaceholderNode = {
                id: childPlaceholderId,
                position: { x: node.position.x, y: node.position.y },
                type: 'placeholder',
                data: { label: '+' },
            };

            const childPlaceholderEdge = {
                id: `${node.id}=>${childPlaceholderId}`,
                source: node.id,
                target: childPlaceholderId,
                type: 'placeholder',
            };

            placeholderNodes.push(childPlaceholderNode);
            placeholderEdges.push(childPlaceholderEdge);
        });

        setNodes(nodes => nodes.concat(placeholderNodes))
        setEdges(edges => edges.concat(placeholderEdges))
    }, [setEdges, setNodes])

    const findLeafNodes = useCallback((nodes: Node[], edges: Edge[]): Node[] => {
        const excludeNodeTypes = ['placeholder', 'evaluatorNode']

        const leafNodes = nodes.filter(node => {
            const outgoers = getOutgoers(node, nodes, edges);

            if (node.type === 'evaluatorNode') {
                const placeholderCount = (node.data?.meta_data?.childCount - outgoers.length) || 0;
                createPlaceholderNodes(Array.from({ length: placeholderCount }, () => node))
            }

            return outgoers.length === 0 && (node?.type && !excludeNodeTypes.includes(node.type));
        });

        return leafNodes;
    }, [createPlaceholderNodes]);

    const findLeafAndCreatePlaceholder = useCallback(() => {
        const nodes_list = getNodes();
        const edges_list = getEdges();

        const leafNodes = findLeafNodes(nodes_list, edges_list)

        createPlaceholderNodes(leafNodes);
    }, [createPlaceholderNodes, findLeafNodes, getEdges, getNodes])

    const handleFileSelect = useCallback(async (fileId: string, filepath: string) => {
        try {
            const response = await getFlowFileFromCommit(filepath, updateFlowId || "");
            if (response === 'undefined') return;
            console.log("flow file " + fileId + " data---> ", filepath);

            if (fileId === FLOW_FILE_PATH.flow) {
                setNodes(response['nodes']);
                setEdges(response['edges']);
                findLeafAndCreatePlaceholder();
            }

            setFileStates(prevData => ({ ...prevData, [fileId]: response }));
            return response;
        }
        catch (error) {
            console.error(`Error fetching content for ${fileId}: `, error);
            alert('Failed to fetch file content.');
        }
    }, [findLeafAndCreatePlaceholder, setEdges, setNodes, updateFlowId])

    const updateRequestNodeOnIntialization = useCallback(async () => {
        const response = await handleFileSelect(FLOW_FILE_PATH.request_schema, FLOW_FILE_PATH.request_schema);
        console.log('SETTING REQUEST NODE DATA', response)

        updateRequestNodeData({ request_schema: JSON.parse(response) });
        console.log('get nodes', getNodes())
    }, [getNodes, handleFileSelect, updateRequestNodeData]);

    const creatFlowWithFiles = async (flow_name: string, message: string, files: ChangeFileOperation[]) => {
        setIsLoading(true);

        let payload: ChangeFilesOptions = { message, files };
        payload = updateFlowId ? { ...payload, branch: flow_name } : { ...payload, branch: "main", new_branch: flow_name };

        try {
            const response = await axiosRepoIntance.repos.repoChangeFiles(OWNER, Repo_Type.flows, payload);
            console.log("create flow response: ", response);
            if (response.status === 201) {
                navigate("/flows");
            }
        } catch (error: any) {
            if (error?.response?.status === 422) {
                return alert("Flow name with " + flow_name + " already exists.");
            }

            alert("some error occurred while creating a Flow.");
            console.log("erron in create flow: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getFlowFileFromCommit = async (filepath: string, commitId: string) => {
        setIsLoading(true);

        try {
            const response = await axiosRepoIntance.repos.repoGetContents(OWNER, Repo_Type.flows, filepath, { ref: commitId });
            // const content = JSON.parse(base64ToUtf8(response.data.content || ""));
            const content = filepath === FLOW_FILE_PATH.flow ?
                JSON.parse(base64ToUtf8(response.data.content || "")) :
                base64ToUtf8(response.data.content || "");

            // Log the content for debugging purposes
            console.log(`Fetched content for ${filepath}: `,);

            return content; // Return the decoded content
        } catch (error: any) {
            if (error?.response?.status === 404) {
                const file_name = filepath.split('/').pop();
                return alert(file_name + " file not found!");
            }

            console.error("Error fetching file content: ", error);
            alert("An error occurred while fetching the file content");
            // throw error; // Rethrow the error to handle it in the calling function
            return "";
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        }
    };

    const getAllCommitsList = async (flowName: string) => {
        try {
            const response = await axiosRepoIntance.repos.repoGetAllCommits(OWNER, Repo_Type.flows, { sha: flowName });
            console.log("list of all commits: ", response.data);
            const list: { commitId: string; created: string, commit: RepoCommit }[] = [];

            response.data.forEach((data: any) => {
                if (data.commit.message === "Initial commit\n") return;

                list.push({ commitId: data.sha, created: data.created, commit: data.commit });
            });

            setCommitsList(list);
            return list;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return alert("Some error occurred, Flow versions not found!");
            }

            alert("some error occured while getting commits list.");
            console.log("error while getting the commits list: ", error);
        }
    };

    const getContentsListForCommit = async (commitId: string) => {
        setIsLoading(true);

        try {
            const response = await axiosRepoIntance.repos.getTree(OWNER, Repo_Type.flows, commitId, { recursive: true })
            return response.data;
        } catch (error: any) {
            alert("some error occured while getting block.py file")
            console.log("some error occured while getting block.py file: ", error)
            return [] as GitTreeResponse;
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        }
    }

    // func saved in reactflow state to update files states on node config save
    const updateFileStates = useCallback((root_schema: JSONSchema7) => {
        const filtered_nodes = getNodes().filter(node => node.type !== 'placeholder');
        const filtered_edges = getEdges().filter(edge => edge.type !== 'placeholder');

        setFileStates(prevData => ({
            ...prevData,
            [FLOW_FILE_PATH.flow]: { nodes: filtered_nodes, edges: filtered_edges },
            [FLOW_FILE_PATH.request_schema]: JSON.stringify(root_schema, null, 2)
        }));
    }, [getEdges, getNodes])

    useEffect(() => {
        storeApi.setState((state) => ({ ...state, updateFileStates }));
    }, [updateFileStates, storeApi]);

    useEffect(() => {
        if (selectedFile !== FLOW_FILE_PATH.flow) return;

        findLeafAndCreatePlaceholder();

        return () => { }
    }, [findLeafAndCreatePlaceholder, selectedFile])

    useEffect(() => {
        //   Fetch all commits for current Flow
        if (!updateFlowId || !updateFlowName) return;

        setBasicInfo((prevInfo) => ({ ...prevInfo, name: updateFlowName }));

        getAllCommitsList(updateFlowName).then((list) => {
            setFlowVersion(updateFlowId);

            if (list) {
                list.forEach(item => {
                    if (item.commitId === updateFlowId) {
                        setBasicInfo((prevInfo) => ({ ...prevInfo, description: item.commit.message || '' }));
                    }
                })
            }
        });

        return () => { };
    }, [updateFlowId, updateFlowName]);

    useEffect(() => {
        resetSourceDataObject();
        return () => { }
    }, []);

    useEffect(() => {
        getContentsListForCommit(updateFlowId || 'main')
            .then((response) => {
                console.log("response: ", response);
                setGitTreeContent(response.tree || []);

                const structuredTree = convertGitTreeToTreeView(response.tree || [], supportedFileType);
                console.log('----------file structure: ', structuredTree)
                setFileStructure(structuredTree);
                setSelectedFile(FLOW_FILE_PATH.flow);
                handleFileSelect(FLOW_FILE_PATH.flow, FLOW_FILE_PATH.flow)
                    .then(() => {
                        const nodes = getNodes()
                        updateSourceDataObject(nodes);
                        console.log('------fetching request schema and updating request node data------');
                        updateRequestNodeOnIntialization();
                    })
            });

        return () => { };
    }, [getNodes, handleFileSelect, updateFlowId, updateRequestNodeOnIntialization]);

    console.log('global======nodes= ', getNodes())
    // console.log('global======edges= ', getEdges())
    console.log('global=======file states: ', fileStates)
    // console.log('=======file structure: ', fileStructure)

    return (
        <PrimaryLayout transparentLoading={isLoading}>
            {/* TOOLBAR */}
            <Box
                pl={2}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                    height: "50px",
                }}
            >
                <Box display="flex" alignItems="center" height="100%" gap={1.5}>
                    <Box>
                        <Typography variant="subtitle2" component="p">
                            {basicInfo.name || "Add a Name"}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            component="p"
                            sx={{
                                maxWidth: 400,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {basicInfo.description || "Add a Description"}
                        </Typography>
                    </Box>

                    <IconButton
                        aria-label="edit-basic-info"
                        onClick={() => setOpenBasicInfoModal(true)}
                    >
                        <EditOutlined />
                    </IconButton>

                    {openBasicInfoModal && (
                        <BasicInfoModal
                            disableName={!!updateFlowId}
                            open={openBasicInfoModal}
                            basicInfo={basicInfo}
                            setOpen={setOpenBasicInfoModal}
                            handleModalSubmit={updateBasicInfo}
                        />
                    )}
                </Box>

                <Box display="flex" alignItems="center" height="100%">
                    <Divider orientation="vertical" flexItem />

                    {updateFlowId && (
                        <>
                            <Box width={150}>
                                <Select
                                    id="flow-version-select"
                                    fullWidth
                                    value={flowVersion}
                                    onChange={(e: SelectChangeEvent) => {
                                        if (!updateFlowId) return;

                                        setFileStates({})
                                        setSelectedFile('')
                                        const newId = e.target.value;
                                        const newPath = location.pathname.replace(
                                            updateFlowId,
                                            newId
                                        );
                                        navigate(newPath, { replace: true });
                                    }}
                                    renderValue={(selected) => selected.slice(0, 10)}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                "& .MuiMenuItem-root": { py: 1 },
                                            },
                                        },
                                        MenuListProps: {
                                            sx: { pt: 0, pb: 0 },
                                        },
                                    }}
                                    sx={{
                                        ".MuiOutlinedInput-notchedOutline": { border: "none" },
                                    }}
                                >
                                    {commitsList.map((item) => (
                                        <MenuItem key={item.commitId} value={item.commitId}>
                                            <Typography variant="body2" width={90}>
                                                {item.commitId.slice(0, 10)}
                                            </Typography>
                                            <Divider
                                                orientation="vertical"
                                                sx={{ borderColor: "#dddddd", mx: 1 }}
                                                flexItem
                                            />
                                            <Typography variant="body2" width={150} textAlign="end">
                                                {getFormattedDate(new Date(item.created))}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <Divider orientation="vertical" flexItem />
                        </>
                    )}

                    {/* <Box px={1}>
                        <Button onClick={() => console.log('====>>>sourceDataObject ', sourceDataObject)}>source object</Button>
                    </Box>

                    <Divider orientation='vertical' flexItem /> */}

                    <Box px={1} display="flex" gap={1.5}>
                        <Button
                            variant="text"
                            onClick={handleSaveFlow}
                            startIcon={<SaveOutlined />}
                        >
                            {updateFlowId ? "update" : "save"}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                const nodes: Node[] = getNodes();
                                const edges: Edge[] = getEdges();

                                console.log("nodes list: ", nodes);
                                console.log("edges list: ", edges);
                                // console.log('file structure: ', JSON.stringify(fileStructure, null, 2))
                                // console.log('file states: ', JSON.stringify(fileStates, null, 2))
                            }}
                            startIcon={<PublishOutlined />}
                        >
                            publish
                        </Button>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box px={1}>
                        <IconButton aria-label="upload-flow">
                            <UploadOutlined />
                        </IconButton>
                        <IconButton aria-label="download-flow">
                            <DownloadOutlined />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* REACT FLOW */}
            <Box>
                <Container
                    sx={{
                        m: 0,
                        padding: "0 !important",
                        maxWidth: "100% !important",
                        height: "calc(100vh - 100px)",
                        display: "flex",
                    }}
                >
                    <Box
                        sx={{
                            width: 240,
                            minWidth: 240,
                            height: "100%",
                            borderRight: "1px solid",
                            borderRightColor: "divider",
                        }}
                    >
                        <Box display="flex" justifyContent="center" gap={1.5}>
                            <CustomTooltip title="Edit">
                                <IconButton size="small"
                                    onClick={handleEditTreeItem}
                                    disabled={Boolean(selectedFile) && Flow_Default_Files.includes(selectedFile)}
                                >
                                    <Edit />
                                </IconButton>
                            </CustomTooltip>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <CustomTooltip title="Add File">
                                <IconButton size="small" onClick={handleAddFileClick}>
                                    <NoteAdd />
                                </IconButton>
                            </CustomTooltip>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <CustomTooltip title="Add Folder">
                                <IconButton size="small" onClick={handleAddFolderClick}>
                                    <CreateNewFolder />
                                </IconButton>
                            </CustomTooltip>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <CustomTooltip title="Delete">
                                <IconButton
                                    onClick={() => setOpenDeleteModalFileStucture(true)}
                                    disabled={Boolean(selectedFile) && Flow_Default_Files.includes(selectedFile)}
                                >
                                    <DeleteForever />
                                </IconButton>
                            </CustomTooltip>

                            <ConfirmationModal
                                open={openDeleteModalFileStucture}
                                setOpen={setOpenDeleteModalFileStucture}
                                headTitle={'Delete ' + selectedFile.split('/').pop()}
                                bodyMessage='Are you sure you want to delete, it cannot be undone?'
                                confirmBtnColor='error'
                                handleSubmit={(isDelete) => {
                                    if (!isDelete) return;
                                    console.log('deleting file: ', selectedFile)
                                    setFileStructure((prevData) => handleDeleteTreeItem(prevData, selectedFile, setItemsToDeletePath));
                                    setOpenDeleteModalFileStucture(false);
                                    deleteFileStatesKeys(selectedFile, setFileStates);
                                    setSelectedFile('');
                                }}
                            />
                        </Box>
                        <Divider />
                        <RichTreeView
                            sx={{ px: 0.5 }}
                            apiRef={apiTreeRef}
                            items={fileStructure}
                            defaultExpandedItems={["block"]}
                            selectedItems={selectedFile}
                            onSelectedItemsChange={(e, nodeId) => {
                                if (nodeId) {
                                    setSelectedFile(nodeId);
                                    console.log("selected file: ", nodeId);

                                    const treeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(nodeId);

                                    if (nodeId.endsWith(".json") && !fileStates[nodeId]) setFileStates((prevData) => ({ ...prevData, [nodeId]: "{}", }));
                                    if (nodeId.endsWith(".md") && !fileStates[nodeId]) setFileStates((prevData) => ({ ...prevData, [nodeId]: "**Hello world!!!**", }));
                                    if (nodeId.endsWith(".txt") && !fileStates[nodeId]) setFileStates((prevData) => ({ ...prevData, [nodeId]: "add your text here...", }));

                                    if (treeItem?.type !== "folder" && treeItem?.path && !fileStates[nodeId]) {
                                        handleFileSelect(nodeId, treeItem?.path);
                                    }

                                    if (nodeId === FLOW_FILE_PATH.request_schema) getRequestSchemaAndUpdateFileStates();
                                    if (nodeId === FLOW_FILE_PATH.flow) {
                                        console.log(fileStates[FLOW_FILE_PATH.request_schema])
                                        // updateRequestNodeData({ request_schema: JSON.parse(fileStates[FLOW_FILE_PATH.request_schema] || '{}') })
                                        // await updateRequestNodeOnFlowSetup();
                                    }
                                }
                            }}
                            // isItemDisabled={(item: CustomTreeViewBaseItem) => !!item.disabled}
                            slots={{ item: CustomTreeItem }}
                            expansionTrigger="iconContainer"
                        />
                    </Box>
                    <Box sx={{ width: "100%" }}>
                        {!selectedFile && (
                            <Box sx={{ height: '70%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                <Report color='warning' fontSize='large' />
                                <Typography variant="h5">
                                    Select a file to view its content.
                                </Typography>
                            </Box>
                        )}

                        {selectedFile &&
                            // !selectedFile.endsWith('.py') &&
                            !selectedFile.endsWith('.md') &&
                            !selectedFile.endsWith('.json') &&
                            !selectedFile.endsWith('.txt') &&
                            selectedFile !== 'Dockerfile' && (
                                <Box sx={{ height: '70%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    <Report color='error' fontSize='large' />
                                    <Typography variant="h5">
                                        Unsupported file type.
                                    </Typography>
                                </Box>
                            )}

                        {selectedFile === FLOW_FILE_PATH.flow && (
                            <ReactFlowPro
                                initNodes={fileStates[FLOW_FILE_PATH.flow]?.nodes || []}
                                initEdges={fileStates[FLOW_FILE_PATH.flow]?.edges || []}
                                nodeChange={(changes: NodeChange[]) => {
                                    if (changes && changes.length === 0) return;
                                    // console.log('----node change triggered-----')

                                    const filtered_nodes = getNodes().filter(node => node.type !== 'placeholder');
                                    const filtered_edges = getEdges().filter(edge => edge.type !== 'placeholder');

                                    setFileStates(prevData => ({ ...prevData, [selectedFile]: { nodes: filtered_nodes, edges: filtered_edges } }))
                                }}
                                onNodeClick={(event, node: Node) => {
                                    event.stopPropagation();

                                    if (node && node?.type && node.type === 'requestNode') {
                                        console.log('request node---> ', node)
                                        setSelectedFile(FLOW_FILE_PATH.request_schema);
                                        getRequestSchemaAndUpdateFileStates();
                                        // !fileStates[FLOW_FILE_PATH.request_schema] && handleFileSelect(FLOW_FILE_PATH.request_schema, FLOW_FILE_PATH.request_schema);
                                    }
                                }}
                            />
                        )}

                        {selectedFile?.endsWith(".json") && selectedFile !== FLOW_FILE_PATH.flow && (
                            <JSONEditor
                                language="json"
                                isSchemaSampleDataOn={false}
                                editorReadOnly={selectedFile === FLOW_FILE_PATH.response_schema}
                                showToolbar={selectedFile !== FLOW_FILE_PATH.response_schema}
                                value={fileStates[selectedFile]}
                                onChange={(value: string | undefined) => {
                                    if (debounceRef.current) clearTimeout(debounceRef.current);

                                    debounceRef.current = setTimeout(() => {
                                        let parsedValue: any;
                                        try {
                                            parsedValue = JSON.parse(value || '{}');
                                        } catch (error) {
                                            parsedValue = {};
                                        }

                                        setFileStates(prevData => ({
                                            ...prevData,
                                            [selectedFile]: value || "{}",
                                            [FLOW_FILE_PATH.flow]: { ...prevData[FLOW_FILE_PATH.flow], nodes: updateFlowStateFile(prevData[FLOW_FILE_PATH.flow].nodes, parsedValue) }
                                        }));
                                    }, 500);
                                }}
                            />
                        )}

                        {selectedFile.endsWith(".md") && (
                            <div data-color-mode="light" style={{ height: '100%', width: '100%' }}>
                                <MDEditor
                                    value={fileStates[selectedFile]}
                                    onChange={(value) => setFileStates(prevData => ({ ...prevData, [selectedFile]: value }))}
                                    height="100%"
                                />
                            </div>
                        )}

                        {selectedFile.endsWith(".txt") && (
                            <JSONEditor
                                language="txt"
                                isSchemaSampleDataOn={false}
                                value={fileStates[selectedFile]}
                                onChange={(value: string | undefined) => {
                                    setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "" }))
                                }}
                            />
                        )}

                        {selectedFile === FLOW_FILE_PATH.dockerfile && (
                            <JSONEditor
                                language="dockerfile"
                                // editorReadOnly={true}
                                isSchemaSampleDataOn={false}
                                value={fileStates[selectedFile]}
                                onChange={(value: string | undefined) => {
                                    setFileStates(prevData => ({ ...prevData, [selectedFile]: value || "" }))
                                }}
                            />
                        )}
                    </Box>
                </Container>

                <AddDialog
                    isDialogOpen={isDialogOpen}
                    isAddingFolder={isAddingFolder}
                    newFolderName={newFolderName}
                    newFileName={newFileName}
                    isEditMode={isEditModeTreeItem}
                    handleCancel={handleCancel}
                    setNewFileName={setNewFileName}
                    setNewFolderName={setNewFolderName}
                    handleCreateNewFolder={() => handleCreateNewFolder(
                        selectedFile,
                        newFolderName,
                        apiTreeRef as React.MutableRefObject<any>,
                        setFileStructure,
                        setIsDialogOpen,
                        setNewFolderName
                    )}
                    handleCreateNewFile={() => handleAddNewFile(
                        selectedFile,
                        newFileName,
                        apiTreeRef as React.MutableRefObject<any>,
                        supportedFileType,
                        setFileStructure,
                        setIsDialogOpen,
                        setNewFileName
                    )}
                    handleUpdateTreeItemName={() => {
                        const newName = handleEditTreeItemCallBack(
                            selectedFile,
                            apiTreeRef as React.MutableRefObject<any>,
                            setFileStructure,
                            isAddingFolder,
                            newFolderName,
                            newFileName,
                            setFileStates,
                            setItemsToDeletePath
                        );
                        if (newName) {
                            setSelectedFile(newName);
                            handleCancel();
                        }
                    }}
                />
            </Box>
        </PrimaryLayout>
    );
};

const AddFlow: React.FC = () => {
    return (
        <ReactFlowProvider>
            <AddFlowWrapper />
        </ReactFlowProvider>
    );
};

export default AddFlow;
